import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import { TESTIMONIALS } from "./data";

interface VsTestimonialsProps {
  className?: string;
}

/**
 * Speaker testimonials from the London Summit — social proof aimed at New York
 * speaker outreach. Driven by `TESTIMONIALS` in `data.ts`; hides itself
 * entirely when that array is empty.
 *
 * Designed to sit inside a `PageRailSection` (the parent adds the horizontal
 * gutter).
 */
export function VsTestimonials({ className }: VsTestimonialsProps) {
  if (TESTIMONIALS.length === 0) return null;

  return (
    <div
      id="testimonials"
      style={{ fontFeatureSettings: VS_FONT_FEATURES }}
      className={cn("scroll-mt-[120px] py-[72px] max-md:py-12", className)}
    >
      <p className="mb-3 text-[12px] tracking-[0.0967em] text-[#95999d] uppercase">
        voices
      </p>
      <h2 className="mb-12 text-[40px] leading-[1.2] font-normal tracking-[-1.6px] text-white max-md:mb-9 max-md:text-[32px] max-md:tracking-[-1.28px]">
        What our attendees had to say about their experience.
      </h2>

      <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {TESTIMONIALS.map((testimonial, i) => (
          <li
            key={`${testimonial.name}-${i}`}
            className="flex flex-col gap-4 rounded-[20px] bg-[#171717] p-6 max-md:p-5"
          >
            <blockquote className="text-[16px] leading-[1.35] font-normal tracking-[-0.01em] text-white max-md:text-[15px]">
              &ldquo;{testimonial.quote}&rdquo;
            </blockquote>
            <div className="mt-auto flex items-center gap-2.5">
              {testimonial.src ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={testimonial.src}
                  alt={testimonial.name}
                  loading="lazy"
                  decoding="async"
                  className="h-8 w-8 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div
                  aria-hidden
                  className="h-8 w-8 shrink-0 rounded-full bg-[#313335]"
                />
              )}
              <div>
                <p className="text-[13px] font-medium text-white">
                  {testimonial.name}
                </p>
                <p className="text-[13px] text-white/55">{testimonial.brand}</p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
